import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { getReconciliation } from "src/api";
import { getInventory } from "src/api/inventory";
import ComboSearch from "src/components/ComboSearch";
import CustomDateRange from "src/components/CustomDateRange";
import CustomSelect from "src/components/CustomSelect";
import { Button } from "src/components/ui/button";
import env from "src/config/env";
import { useDashboardContext } from "src/context/DashboardContext";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import { getCustomizationValue, getFromLocalStorage, renderCurrency, renderNumber } from "src/lib/utils";
import { useAuth } from "src/providers/AuthProvider";

const Reconciliation = () => {
	const { user } = useAuth();
	const { entities } = useDashboardContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const entity = searchParams.get("entity") ?? "";
	const product = searchParams.get("product") ?? "";
	const start_date = searchParams.get("start_date");
	const end_date = searchParams.get("end_date");

	const { data } = useQuery({
		queryKey: ["reconciliation", entity, start_date, end_date, product],
		queryFn: () => getReconciliation({ ...(entity && { entity }), start_date, end_date, product }),
		suspense: true,
	});

	const updateSearchParams = (key, value) => {
		const newSearchParams = new URLSearchParams(searchParams);

		if (value === "0") {
			newSearchParams.delete(key);
		} else {
			newSearchParams.set(key, value);
		}

		setSearchParams(newSearchParams);
	};

	const { mutate, isLoading } = useMutation({
		mutationFn: async () => {
			const access_token = getFromLocalStorage("access_token");

			return await axios.get(`${env.REACT_APP_API_BASE_URL}/reconciliation/export`, {
				headers: { Authorization: `Bearer ${access_token}` },
				responseType: "blob",
			});
		},
		onSuccess: (response) => {
			const blob = new Blob([response.data]);
			const url = window.URL.createObjectURL(blob);

			const link = document.createElement("a");
			link.href = url;

			const fileName = "reconciliation_data.xlsx";

			link.setAttribute("download", fileName);

			document.body.appendChild(link);
			link.click();
			link.remove();

			window.URL.revokeObjectURL(url);
		},
	});

	const statistics = data?.data;

	return (
		<div className="pageWrapper">
			<div className="pageTop tw-flex tw-gap-4 tw-items-center tw-justify-between tw-flex-wrap">
				<h2>Reconciliation</h2>
				<div className="tw-gap-4 tw-flex tw-items-center tw-flex-wrap">
					<Button onClick={mutate} disabled={isLoading}>
						Export data
					</Button>
				</div>
			</div>

			<div className="innerContent">
				<div className="tw-grid tw-gap-4 tw-grid-cols-[repeat(auto-fit,minmax(300px,1fr))] tw-mb-7">
					<CustomSelect
						placeholder="Select Store/Warehouse"
						value={entity}
						className="tw-h-auto"
						onChange={(value) => updateSearchParams("entity", value)}
						options={[{ value: "0", label: "All" }, ...entities?.map(({ name, uuid }) => ({ value: uuid, label: name }))]}
					/>
					<ComboSearch
						value={product}
						onChange={(value, option) => updateSearchParams("product", value)}
						placeholder="Search product..."
						className="tw-justify-between tw-w-full"
						optionRenderer={({ name, sku }) => (
							<div className="flex gap-2">
								<p>
									{name} (SKU: {sku})
								</p>
							</div>
						)}
						queryKey="items"
						queryFN={getInventory}
						mapFN={(option) => ({
							label: option.name,
							value: String(option.id),
							info: option,
						})}
					/>
					<CustomDateRange />
					<Button onClick={() => setSearchParams({})} className="!tw-py-3">
						Clear Filters
					</Button>
				</div>
				<div className="tw-grid tw-gap-x-4 tw-gap-y-6 tw-grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
					<div className="tw-bg-white tw-rounded-xl tw-shadow-[0_0_5px_rgba(0,0,0,0.2)] tw-p-5">
						<div className="tw-font-semibold tw-text-green tw-text-[16px]">Total Revenue</div>
						<div className="tw-text-[22px] tw-text-black tw-font-semibold">{renderCurrency(statistics?.total_revenue)}</div>
					</div>

					<div className="tw-bg-white tw-rounded-xl tw-shadow-[0_0_5px_rgba(0,0,0,0.2)] tw-p-5">
						<div className="tw-font-semibold tw-text-green tw-text-[16px]">Cost of Sales</div>
						<div className="tw-text-[22px] tw-text-black tw-font-semibold">{renderCurrency(statistics?.sales_cost)}</div>
					</div>

					<div className="tw-bg-white tw-rounded-xl tw-shadow-[0_0_5px_rgba(0,0,0,0.2)] tw-p-5">
						<div className="tw-font-semibold tw-text-green tw-text-[16px]">Gross Profit</div>
						<div className="tw-text-[22px] tw-text-black tw-font-semibold">{renderCurrency(statistics?.gross_profit)}</div>
					</div>

					<div className="tw-bg-white tw-rounded-xl tw-shadow-[0_0_5px_rgba(0,0,0,0.2)] tw-p-5">
						<div className="tw-font-semibold tw-text-green tw-text-[16px]">
							{getCustomizationValue(user, "inventory_label", true)} Worth
						</div>
						<div className="tw-text-[22px] tw-text-black tw-font-semibold">
							{renderCurrency(statistics?.inventory_selling_price_worth)}
						</div>
						<div className="tw-text-[#E20B0B] tw-text-[11px]">By Selling Price</div>
					</div>

					<div className="tw-bg-white tw-rounded-xl tw-shadow-[0_0_5px_rgba(0,0,0,0.2)] tw-p-5">
						<div className="tw-font-semibold tw-text-green tw-text-[16px]">Total Number of Transactions</div>
						<div className="tw-text-[22px] tw-text-black tw-font-semibold">{renderNumber(statistics?.total_sales_count)}</div>
					</div>

					<div className="tw-bg-white tw-rounded-xl tw-shadow-[0_0_5px_rgba(0,0,0,0.2)] tw-p-5">
						<div className="tw-font-semibold tw-text-green tw-text-[16px]">
							Avg. Order Per {getCustomizationValue(user, "customer_label", true)}
						</div>
						<div className="tw-text-[22px] tw-text-black tw-font-semibold">{renderCurrency(statistics?.avg_customer_order)}</div>
					</div>

					<div className="tw-bg-white tw-rounded-xl tw-shadow-[0_0_5px_rgba(0,0,0,0.2)] tw-p-5">
						<div className="tw-font-semibold tw-text-green tw-text-[16px]">
							Debts Owed by {getCustomizationValue(user, "customer_label")}
						</div>
						<div className="tw-text-[22px] tw-text-black tw-font-semibold">{renderCurrency(statistics?.debt_owed)}</div>
					</div>

					<Link to="/inventory" className="tw-bg-white tw-rounded-xl tw-shadow-[0_0_5px_rgba(0,0,0,0.2)] tw-p-5">
						<div className="tw-font-semibold tw-text-green tw-text-[16px]">
							{getCustomizationValue(user, "inventory_label", true)} Worth
						</div>
						<div className="tw-text-[22px] tw-text-black tw-font-semibold">{renderCurrency(statistics?.inventory_cost_price_worth)}</div>
						<div className="tw-text-[#E20B0B] tw-text-[11px]">By Cost Price</div>
					</Link>
				</div>
			</div>
		</div>
	);
};

const Comp = withPermissionWrapper(Reconciliation, "view_reconciliation");
export default Comp;
